"use client";

import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import CurationEmptyState from "@/components/brain/my-stream/curations/CurationEmptyState";
import { Spinner } from "@/components/dotLoader/DotLoader";
import { TweetPreviewModeProvider } from "@/components/tweets/TweetPreviewModeContext";
import CommonIntersectionElement from "@/components/utils/CommonIntersectionElement";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useCurationManagementPermission } from "@/hooks/useCurationManagementPermission";
import { useDropCurationMembershipMutation } from "@/hooks/drops/useDropCurationMembershipMutation";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import { useWaveDrops } from "@/hooks/useWaveDrops";
import type { ApiWave } from "@/generated/models/ApiWave";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, type ReactNode } from "react";
import { useLayout } from "../layout/LayoutContext";
interface MyStreamWaveCurationContentProps {
  readonly wave: ApiWave;
  readonly curationId: string;
  readonly curationName?: string | null | undefined;
  readonly onDropClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly constrainToViewport?: boolean | undefined;
}
function MyStreamWaveCurationDropItem(t0) {
  const $ = _c(25);
  const {
    drop,
    previousDrop,
    nextDrop,
    curationId,
    canManageActiveCuration,
    onDropClick
  } = t0;
  const {
    hasTouchScreen,
    isApp
  } = useDeviceInfo();
  const isTouchDevice = useIsTouchDevice();
  let t1;
  if ($[0] !== drop.id) {
    t1 = {
      dropId: drop.id
    };
    $[0] = drop.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    updateMembership,
    isPending
  } = useDropCurationMembershipMutation(t1);
  const shouldUseDetachedRemoveButton = isTouchDevice || isApp && hasTouchScreen;
  let t2;
  if ($[2] !== curationId || $[3] !== updateMembership) {
    t2 = () => {
      updateMembership(curationId, "remove");
    };
    $[2] = curationId;
    $[3] = updateMembership;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRemove = t2;
  const t3 = `tw-group tw-relative ${canManageActiveCuration && shouldUseDetachedRemoveButton ? "tw-pt-5" : ""}`;
  let t4;
  if ($[5] !== canManageActiveCuration || $[6] !== handleRemove || $[7] !== isPending || $[8] !== shouldUseDetachedRemoveButton) {
    t4 = canManageActiveCuration && shouldUseDetachedRemoveButton && <button type="button" onClick={event => {
      event.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" className="tw-absolute tw-right-5 tw-top-0 tw-z-20 tw-inline-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-300 tw-transition-colors tw-duration-200 active:tw-text-iron-100 disabled:tw-cursor-not-allowed disabled:tw-opacity-60">{isPending ? <Spinner dimension={12} /> : <XMarkIcon className="tw-size-4 tw-flex-shrink-0" />}</button>;
    $[5] = canManageActiveCuration;
    $[6] = handleRemove;
    $[7] = isPending;
    $[8] = shouldUseDetachedRemoveButton;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== drop || $[11] !== nextDrop || $[12] !== onDropClick || $[13] !== previousDrop) {
    t5 = <Drop key={drop.stableKey} drop={drop} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={false} activeDrop={null} showReplyAndQuote={false} location={DropLocation.WAVE} dropViewDropId={null} onReply={_temp} onReplyClick={_temp2} onQuoteClick={_temp3} onDropContentClick={onDropClick} />;
    $[10] = drop;
    $[11] = nextDrop;
    $[12] = onDropClick;
    $[13] = previousDrop;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== canManageActiveCuration || $[16] !== handleRemove || $[17] !== isPending || $[18] !== shouldUseDetachedRemoveButton) {
    t6 = canManageActiveCuration && !shouldUseDetachedRemoveButton && <button type="button" onClick={event_0 => {
      event_0.stopPropagation();
      handleRemove();
    }} disabled={isPending} aria-label="Remove drop from this curation" title="Remove from wave" className="tw-absolute tw-right-7 tw-top-4 tw-z-20 tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-black/50 tw-p-0 tw-text-iron-400 tw-shadow-[0_10px_30px_rgba(0,0,0,0.32)] tw-backdrop-blur-sm tw-transition-all tw-duration-200 tw-ease-out hover:tw-border-rose-500/30 hover:tw-bg-rose-500/20 hover:tw-text-rose-300 active:tw-bg-rose-500/15 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 desktop-hover:tw-pointer-events-none desktop-hover:tw-w-auto desktop-hover:tw-translate-y-1 desktop-hover:tw-gap-1.5 desktop-hover:tw-px-2.5 desktop-hover:tw-text-xs desktop-hover:tw-font-medium desktop-hover:tw-opacity-0 desktop-hover:group-hover:tw-pointer-events-auto desktop-hover:group-hover:tw-translate-y-0 desktop-hover:group-hover:tw-opacity-100">{isPending ? <><Spinner dimension={12} /><span className="tw-hidden desktop-hover:tw-inline">Removing</span></> : <><XMarkIcon className="tw-size-4 tw-flex-shrink-0 desktop-hover:tw-size-3.5" /><span className="tw-hidden desktop-hover:tw-inline">Remove</span></>}</button>;
    $[15] = canManageActiveCuration;
    $[16] = handleRemove;
    $[17] = isPending;
    $[18] = shouldUseDetachedRemoveButton;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}
function _temp3() {}
function _temp2() {}
function _temp() {}
export default function MyStreamWaveCurationContent(t0) {
  const $ = _c(32);
  const {
    wave,
    curationId,
    curationName,
    onDropClick,
    constrainToViewport: t1
  } = t0;
  const constrainToViewport = t1 === undefined ? true : t1;
  const {
    leaderboardViewStyle
  } = useLayout();
  let t2;
  if ($[0] !== curationId || $[1] !== wave.id) {
    t2 = {
      waveId: wave.id,
      curationId
    };
    $[0] = curationId;
    $[1] = wave.id;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDrops(t2);
  const permissionProbeDropId = drops[0]?.id ?? "";
  let t3;
  if ($[3] !== curationId || $[4] !== permissionProbeDropId) {
    t3 = {
      curationId,
      probeDropId: permissionProbeDropId
    };
    $[3] = curationId;
    $[4] = permissionProbeDropId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const canManageActiveCuration = useCurationManagementPermission(t3);
  const isInitialLoading = isFetching && drops.length === 0;
  let t4;
  if ($[6] !== fetchNextPage || $[7] !== hasNextPage || $[8] !== isFetchingNextPage) {
    t4 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage();
    };
    $[6] = fetchNextPage;
    $[7] = hasNextPage;
    $[8] = isFetchingNextPage;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleBottomIntersection = t4;
  let t5;
  if ($[10] !== curationName) {
    t5 = curationName?.trim() ?? "Curation";
    $[10] = curationName;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const curationTitle = t5;
  let t6;
  if ($[12] !== canManageActiveCuration || $[13] !== curationId || $[14] !== drops || $[15] !== onDropClick) {
    t6 = drops.map((drop, index) => <MyStreamWaveCurationDropItem key={drop.stableKey} drop={drop} previousDrop={index > 0 ? drops[index - 1] ?? null : null} nextDrop={drops[index + 1] ?? null} curationId={curationId} canManageActiveCuration={canManageActiveCuration} onDropClick={onDropClick} />);
    $[12] = canManageActiveCuration;
    $[13] = curationId;
    $[14] = drops;
    $[15] = onDropClick;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const renderedDrops = t6;
  let content;
  if (isInitialLoading) {
    let t7;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    content = t7;
  } else {
    if (drops.length === 0) {
      const t7 = constrainToViewport ? "tw-flex tw-flex-1 tw-items-center tw-justify-center tw-px-6" : undefined;
      let t8;
      if ($[18] !== curationTitle || $[19] !== t7) {
        t8 = <CurationEmptyState curationTitle={curationTitle} containerClassName={t7} />;
        $[18] = curationTitle;
        $[19] = t7;
        $[20] = t8;
      } else {
        t8 = $[20];
      }
      content = t8;
    } else {
      let t7;
      if ($[21] !== handleBottomIntersection || $[22] !== hasNextPage || $[23] !== isFetchingNextPage) {
        t7 = (hasNextPage || isFetchingNextPage) && <div className="tw-py-4">{isFetchingNextPage ? <div className="tw-flex tw-justify-center"><CircleLoader size={CircleLoaderSize.MEDIUM} /></div> : <CommonIntersectionElement onIntersection={handleBottomIntersection} />}</div>;
        $[21] = handleBottomIntersection;
        $[22] = hasNextPage;
        $[23] = isFetchingNextPage;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      let t8;
      if ($[25] !== renderedDrops || $[26] !== t7) {
        t8 = <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">{renderedDrops}{t7}</div>;
        $[25] = renderedDrops;
        $[26] = t7;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      content = t8;
    }
  }
  const t7 = constrainToViewport ? "tw-flex tw-h-full tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-grow tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300" : "tw-flex tw-min-h-0 tw-w-full tw-min-w-0 tw-flex-col";
  const t8 = constrainToViewport ? leaderboardViewStyle : undefined;
  let t9;
  if ($[28] !== content || $[29] !== t7 || $[30] !== t8) {
    t9 = <TweetPreviewModeProvider mode="never"><div className={t7} style={t8}>{content}</div></TweetPreviewModeProvider>;
    $[28] = content;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}